import { Check } from 'lucide-react';
import { INVOICE_TEMPLATES, type TemplateId } from './InvoiceTemplates';

interface TemplatePickerProps {
  selectedId: TemplateId;
  onChange: (id: TemplateId) => void;
}

export function TemplatePicker({ selectedId, onChange }: TemplatePickerProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {INVOICE_TEMPLATES.map((tpl) => {
        const isActive = tpl.id === selectedId;

        return (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onChange(tpl.id)}
            className={[
              'relative rounded-xl overflow-hidden border-2 transition-all duration-200 text-left bg-white',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500',
              isActive
                ? 'border-indigo-500 shadow-md scale-[1.02]'
                : 'border-slate-200 hover:border-slate-350 hover:shadow-sm hover:scale-[1.01]',
            ].join(' ')}
            aria-label={`Select ${tpl.name} layout`}
            aria-pressed={isActive}
          >
            {/* Layout Wireframe Swatch */}
            <div className="w-full h-16 bg-slate-50/50 border-b border-slate-100 flex items-center justify-center p-3 relative overflow-hidden select-none">
              {tpl.id === 'none' && (
                /* Minimalist: standard block */
                <div className="w-full h-full flex flex-col justify-between opacity-50">
                  <div className="flex justify-between items-center">
                    <div className="w-8 h-1.5 bg-slate-400 rounded-sm" />
                    <div className="w-10 h-2 bg-slate-500 rounded-sm" />
                  </div>
                  <div className="w-12 h-2.5 bg-slate-300 rounded-sm" />
                  <div className="space-y-1">
                    <div className="w-full h-1 bg-slate-400 rounded-sm" />
                    <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                    <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                  </div>
                </div>
              )}

              {tpl.id === 'ocean' && (
                /* Corporate Accent: Left Bar Accent */
                <div className="w-full h-full flex flex-col justify-between opacity-50">
                  <div className="flex justify-between items-center">
                    <div className="w-10 h-2.5 bg-slate-500 rounded-sm" />
                    <div className="w-8 h-2 bg-slate-400 rounded-sm" />
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <div className="w-0.5 h-3.5 bg-slate-600 rounded" />
                    <div className="w-10 h-2.5 bg-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1">
                    <div className="w-full h-1.5 bg-slate-400 rounded-sm" />
                    <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                  </div>
                </div>
              )}

              {tpl.id === 'midnight' && (
                /* Modern Executive: Full top header strip */
                <div className="w-full h-full flex flex-col justify-between relative opacity-50">
                  <div className="absolute top-0 left-0 right-0 h-4 bg-slate-650 rounded-t-sm flex items-center justify-between px-1.5">
                    <div className="w-4 h-1 bg-slate-400 rounded-sm" />
                    <div className="w-6 h-1.5 bg-slate-300 rounded-sm" />
                  </div>
                  <div className="pt-5 flex justify-between items-center">
                    <div className="w-10 h-2 bg-slate-300 rounded-sm" />
                    <div className="w-8 h-2 bg-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1">
                    <div className="w-full h-1 bg-slate-400 rounded-sm" />
                    <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                  </div>
                </div>
              )}

              {tpl.id === 'forest' && (
                /* Left Sidebar layout wireframe */
                <div className="w-full h-full flex gap-1.5 opacity-50">
                  <div className="w-1/4 h-full bg-slate-100 border-r border-slate-250 p-1 flex flex-col justify-between rounded-l-sm">
                    <div className="w-full h-1.5 bg-slate-400 rounded-sm" />
                    <div className="space-y-0.5">
                      <div className="w-full h-0.5 bg-slate-300 rounded-sm" />
                      <div className="w-full h-0.5 bg-slate-300 rounded-sm" />
                    </div>
                  </div>
                  <div className="w-3/4 h-full flex flex-col justify-between">
                    <div className="w-10 h-2 bg-slate-400 rounded-sm" />
                    <div className="space-y-1">
                      <div className="w-full h-1 bg-slate-350 rounded-sm" />
                      <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                    </div>
                  </div>
                </div>
              )}

              {tpl.id === 'rose' && (
                /* Elegant Classic: Serif double border look */
                <div className="w-full h-full flex flex-col justify-between items-center opacity-50">
                  <div className="w-10 h-1.5 bg-slate-400 rounded-sm" />
                  <div className="w-12 h-2.5 bg-slate-300 rounded-sm" />
                  <div className="w-full py-0.5 border-y border-slate-450 flex flex-col gap-0.5">
                    <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                  </div>
                  <div className="w-full h-0.5 bg-slate-200 rounded-sm" />
                </div>
              )}
            </div>

            {/* Label */}
            <div className="bg-white px-2.5 py-1.5">
              <p className="text-xs font-bold text-slate-800 truncate">{tpl.name}</p>
              <p className="text-[10px] text-slate-500 truncate mt-0.5 leading-none">{tpl.description}</p>
            </div>

            {/* Selected badge */}
            {isActive && (
              <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center shadow z-10">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
